import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { requete } from '../../api/client';
import { messageErreur } from '../../api/erreurs';
import { useSession } from '../../session/FournisseurSession';
interface Suivi {
  montant_demande: string;
  total_valide: string;
  total_reserve: string | null;
  elements: {
    id: string;
    montant: string;
    financeur: string;
    reference_decision: string;
    motif: string;
    statut: string;
    propose_par: string | null;
    motif_controle: string | null;
  }[];
}
const etats: Record<string, string> = {
  PENDING: 'À contrôler',
  APPROVED: 'Enregistrement validé',
  REJECTED: 'Enregistrement rejeté',
};
const montant = (v: string) => BigInt(v).toLocaleString('fr-FR') + ' XAF';
export function FinancementsInstruction({
  id,
  preparer,
  controler,
}: {
  id: string;
  preparer: boolean;
  controler: boolean;
}) {
  const { contexte } = useSession();
  const client = useQueryClient();
  const [ouvert, setOuvert] = useState(false);
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState('');
  const cle = ['financements-instruction', contexte?.appartenance_active?.id, id];
  const chemin = `/instruction-investissements/${id}/financements`;
  const q = useQuery({ queryKey: cle, queryFn: () => requete<Suivi>(chemin), enabled: ouvert });
  const solde = q.data
    ? BigInt(q.data.montant_demande) - BigInt(q.data.total_reserve ?? q.data.total_valide)
    : 0n;
  async function envoyer(url: string, corps: unknown) {
    setErreur('');
    setOccupe(true);
    try {
      await requete(url, { methode: 'POST', corps });
      await client.invalidateQueries({ queryKey: cle });
      return true;
    } catch (e) {
      setErreur(messageErreur(e));
      return false;
    } finally {
      setOccupe(false);
    }
  }
  return (
    <details onToggle={(e) => setOuvert(e.currentTarget.open)}>
      <summary>Suivi du financement</summary>
      <p>
        Enregistrement interne des références de financement. La validation de cette saisie ne
        déclenche aucun paiement et ne remplace pas la décision officielle du financeur.
      </p>
      {(erreur || q.error) && <p role="alert">{erreur || messageErreur(q.error)}</p>}
      {ouvert && q.isPending && <p>Chargement du financement…</p>}
      {q.data && (
        <>
          <p>
            Montant demandé : {montant(q.data.montant_demande)} · Enregistré et validé :{' '}
            <strong>{montant(q.data.total_valide)}</strong>
          </p>
          {q.data.total_reserve !== null && (
            <p>Solde disponible, propositions en attente déduites : {montant(solde.toString())}</p>
          )}
          {q.data.elements.length === 0 && <p>Aucun financement visible enregistré.</p>}
          <ul style={{ paddingLeft: 20 }}>
            {q.data.elements.map((f) => (
              <li key={f.id} style={{ marginBlock: 16 }}>
                <strong>
                  {f.financeur} · {montant(f.montant)}
                </strong>
                <p>
                  {etats[f.statut]} · Référence : {f.reference_decision}
                </p>
                <p>{f.motif}</p>
                {f.motif_controle && <p>Contrôle : {f.motif_controle}</p>}
                {controler && f.statut === 'PENDING' && f.propose_par !== contexte?.compte.id && (
                  <form
                    style={{ display: 'grid', gap: 12, maxWidth: 700 }}
                    onSubmit={(e) => {
                      e.preventDefault();
                      const d = new FormData(e.currentTarget);
                      void envoyer(`${chemin}/${f.id}/controle`, {
                        decision: d.get('decision'),
                        motif: d.get('motif'),
                      });
                    }}
                  >
                    <label>
                      Décision sur le financement{' '}
                      <select name="decision">
                        <option value="approuver">Valider l’enregistrement</option>
                        <option value="rejeter">Rejeter l’enregistrement</option>
                      </select>
                    </label>
                    <label>
                      Motivation du contrôle financier{' '}
                      <textarea name="motif" required minLength={20} maxLength={4000} rows={3} />
                    </label>
                    <button disabled={occupe}>Contrôler le financement</button>
                  </form>
                )}
              </li>
            ))}
          </ul>
          {preparer && solde > 0n && (
            <form
              style={{ display: 'grid', gap: 12, maxWidth: 700 }}
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const d = new FormData(form);
                if (
                  await envoyer(chemin, {
                    montant: Number(d.get('montant')),
                    financeur: d.get('financeur'),
                    reference_decision: d.get('reference_decision'),
                    motif: d.get('motif'),
                  })
                )
                  form.reset();
              }}
            >
              <h3>Proposer un enregistrement de financement</h3>
              <label>
                Financeur <input name="financeur" required minLength={3} maxLength={200} />
              </label>
              <label>
                Référence de la décision{' '}
                <input name="reference_decision" required minLength={3} maxLength={200} />
              </label>
              <label>
                Montant du financement en XAF{' '}
                <input name="montant" type="number" required min={1} max={Number(solde)} step={1} />
              </label>
              <label>
                Justification du financement{' '}
                <textarea name="motif" required minLength={20} maxLength={4000} rows={3} />
              </label>
              <button disabled={occupe}>Soumettre le financement au contrôle</button>
            </form>
          )}
        </>
      )}
    </details>
  );
}
